import type {
  EquipmentEvidenceSource,
  EvidenceTier,
  SourceKind,
} from "./seedSchema";

const VERIFIED_ON = "2026-07-23";
const NEXT_QUARTERLY_REVIEW = "2026-10-23";

interface SourceInput {
  id: string;
  publisher: string;
  title: string;
  url: string;
  kind: SourceKind;
  evidenceTier?: EvidenceTier;
  notes?: string;
}

const oemSource = ({
  evidenceTier = "tier-1-oem-primary",
  ...input
}: SourceInput): EquipmentEvidenceSource => ({
  ...input,
  evidenceTier,
  lastVerifiedOn: VERIFIED_ON,
  nextReviewOn: NEXT_QUARTERLY_REVIEW,
  rights: {
    posture: "facts-only-no-document-redistribution",
    licenseOrTerms:
      "Publisher copyright and website terms; no redistribution license inferred.",
    allowedUse:
      "Curate attributed, non-copyrightable product facts and links after human review.",
    prohibitedUse:
      "Do not mirror brochures, photography, logos, drawings, or substantial publisher wording.",
  },
});

const publicSource = (
  input: SourceInput,
  licenseOrTerms: string,
): EquipmentEvidenceSource => ({
  ...input,
  evidenceTier: input.evidenceTier ?? "tier-3-government-or-lab",
  lastVerifiedOn: VERIFIED_ON,
  nextReviewOn: NEXT_QUARTERLY_REVIEW,
  rights: {
    posture:
      licenseOrTerms === "Public domain (United States government work)."
        ? "public-domain"
        : "terms-review-required",
    licenseOrTerms,
    allowedUse:
      "Use attributed facts and dataset identifiers subject to the stated source terms.",
    prohibitedUse:
      "Do not imply agency endorsement; review source terms before redistributing source files.",
  },
});

const openSource = (
  input: SourceInput,
  licenseOrTerms: string,
): EquipmentEvidenceSource => ({
  ...input,
  evidenceTier: input.evidenceTier ?? "tier-3-government-or-lab",
  lastVerifiedOn: VERIFIED_ON,
  nextReviewOn: NEXT_QUARTERLY_REVIEW,
  rights: {
    posture: "open-source",
    licenseOrTerms,
    allowedUse:
      "Use and redistribute in accordance with the named license, preserving required notices.",
    prohibitedUse:
      "Do not remove license notices or imply publisher endorsement.",
  },
});

/**
 * Source and rights manifest for the OEM seed and future enrichment feeds.
 *
 * Rights fields are a conservative ingestion policy, not legal advice. The
 * library stores URLs and extracted facts, never third-party source documents.
 */
export const EQUIPMENT_SOURCE_LICENSE_MANIFEST = [
  oemSource({
    id: "ge-vernova-gas-catalog-2025",
    publisher: "GE Vernova",
    title: "GE Vernova Gas Power Catalog",
    url: "https://www.gevernova.com/content/dam/gepower-new/global/en_US/downloads/noindexpdf/GEA35241-GE-Vernova-Gas-Power-Catalog.pdf",
    kind: "oem-brochure",
  }),
  oemSource({
    id: "siemens-energy-gas-turbines",
    publisher: "Siemens Energy",
    title: "Gas turbines",
    url: "https://www.siemens-energy.com/global/en/home/products-services/product-offerings/gas-turbines.html",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "mitsubishi-power-gas-lineup",
    publisher: "Mitsubishi Power",
    title: "Gas turbine product lineup",
    url: "https://power.mhi.com/products/gasturbines/lineup",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "mitsubishi-power-aeroderivative-lineup",
    publisher: "Mitsubishi Power",
    title: "Aero-derivative gas turbines",
    url: "https://power.mhi.com/products/aerogasturbines",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "solar-turbines-ratings",
    publisher: "Solar Turbines",
    title: "Gas turbine ratings",
    url: "https://www.solarturbines.com/en_US/products/turbine-ratings.html",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "siemens-energy-industrial-steam",
    publisher: "Siemens Energy",
    title: "Industrial steam turbines",
    url: "https://www.siemens-energy.com/global/en/home/products-services/product/industrial-steam-turbines.html",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "siemens-energy-utility-steam",
    publisher: "Siemens Energy",
    title: "Utility steam turbines",
    url: "https://www.siemens-energy.com/global/en/home/products-services/product/utility-steam-turbines.html",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "ge-vernova-stf-a200",
    publisher: "GE Vernova",
    title: "STF-A200 steam turbine",
    url: "https://www.gevernova.com/gas-power/products/steam-turbines/stf-a200",
    kind: "oem-product-page",
  }),
  oemSource({
    id: "ge-vernova-stf-d600",
    publisher: "GE Vernova",
    title: "600 series steam turbines",
    url: "https://www.gevernova.com/gas-power/products/steam-turbines/600-series",
    kind: "oem-product-page",
  }),
  oemSource({
    id: "wartsila-engine-products",
    publisher: "Wärtsilä",
    title: "Engine generating sets",
    url: "https://www.wartsila.com/energy/engine-power-plant-solutions/engine-power-plant-products/products",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "innio-jenbacher-portfolio",
    publisher: "INNIO Jenbacher",
    title: "Jenbacher gas engines portfolio",
    url: "https://www.innio.com/en/products/jenbacher/gas-engines/",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "innio-j920-flextra",
    publisher: "INNIO Jenbacher",
    title: "J920 FleXtra product brochure",
    url: "https://innio.com/images/medias/files/170/innio_j920_flextra_broschuere_update_2020_en_screen_ijb-120012-en.pdf",
    kind: "oem-brochure",
  }),
  oemSource({
    id: "cat-gas-generator-sets",
    publisher: "Caterpillar",
    title: "Gas generator sets",
    url: "https://www.cat.com/en_US/products/new/power-systems/electric-power/gas-generator-sets.html",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "cat-cg260-16",
    publisher: "Caterpillar",
    title: "CG260-16 gas generator set",
    url: "https://www.cat.com/en_US/products/new/power-systems/electric-power/gas-generator-sets/15969826.html",
    kind: "oem-product-page",
  }),
  oemSource({
    id: "vestas-product-portfolio",
    publisher: "Vestas",
    title: "Wind turbine product portfolio",
    url: "https://www.vestas.com/en/energy-solutions",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "vestas-v236",
    publisher: "Vestas",
    title: "V236-15.0 MW offshore wind turbine",
    url: "https://www.vestas.com/en/energy-solutions/offshore-wind-turbines/V236-15MW",
    kind: "oem-product-page",
  }),
  oemSource({
    id: "siemens-gamesa-wind-portfolio",
    publisher: "Siemens Gamesa",
    title: "Wind turbine products and services",
    url: "https://www.siemensgamesa.com/global/en/home/products-and-services.html",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "nordex-product-portfolio",
    publisher: "Nordex Group",
    title: "Wind turbine product portfolio",
    url: "https://www.nordex-online.com/en/product/",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "ge-vernova-wind-portfolio",
    publisher: "GE Vernova",
    title: "Wind turbines",
    url: "https://www.gevernova.com/wind-power/wind-turbines",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "first-solar-products",
    publisher: "First Solar",
    title: "Series 6 and Series 7 photovoltaic modules",
    url: "https://www.firstsolar.com/Products",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "maxeon-technical-documents",
    publisher: "Maxeon Solar Technologies",
    title: "Solar panel technical documents",
    url: "https://www.maxeon.com/technical-documents",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "sungrow-sg350hx",
    publisher: "Sungrow",
    title: "SG350HX datasheet",
    url: "https://info-support.sungrowpower.com/application/pdf/2025/06/27/DS_20250617_SG350HX_Datasheet_V23_EN.pdf",
    kind: "oem-datasheet",
  }),
  oemSource({
    id: "sma-sunny-central-up",
    publisher: "SMA Solar Technology",
    title: "Sunny Central UP",
    url: "https://www.sma.de/produkte/solar-wechselrichter/sunny-central-up",
    kind: "oem-product-page",
  }),
  oemSource({
    id: "voith-hydro-turbines",
    publisher: "Voith",
    title: "Hydro turbines",
    url: "https://www.voith.com/corp-en/products-services/hydropower-components/turbines.html",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "voith-small-hydro",
    publisher: "Voith",
    title: "Small hydro products and services",
    url: "https://www.voith.com/corp-en/hydropower-components/small-hydro-products-services.html",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "voith-hycomplete",
    publisher: "Voith",
    title: "HyComplete and M-Line hydro solutions",
    url: "https://www.voith.com/corp-en/hydropower-components/hycomplete.html",
    kind: "oem-product-page",
  }),
  oemSource({
    id: "valmet-cfb-boilers",
    publisher: "Valmet",
    title: "Circulating fluidized bed boilers",
    url: "https://www.valmet.com/energyproduction/cfb-boilers/",
    kind: "oem-product-page",
  }),
  oemSource({
    id: "valmet-bfb-boilers",
    publisher: "Valmet",
    title: "Bubbling fluidized bed boilers",
    url: "https://www.valmet.com/energyproduction/bfb-boilers/",
    kind: "oem-product-page",
  }),
  oemSource({
    id: "ge-vernova-hrsg",
    publisher: "GE Vernova",
    title: "Heat recovery steam generators",
    url: "https://www.gevernova.com/gas-power/products/hrsg",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "john-cockerill-hrsg",
    publisher: "John Cockerill",
    title: "Heat recovery steam generators",
    url: "https://energy.johncockerill.com/en/boilers-and-heat-recovery-steam-generators/heat-recovery-steam-generators/",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "john-cockerill-boilers",
    publisher: "John Cockerill",
    title: "Boilers and heat recovery steam generators",
    url: "https://energy.johncockerill.com/en/boilers-and-heat-recovery-steam-generators/",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "turboden-orc",
    publisher: "Turboden",
    title: "Organic Rankine Cycle systems",
    url: "https://www.turboden.com/products/2463/orc-system",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "aalborg-csp-receiver",
    publisher: "Aalborg CSP",
    title: "Solar tower receiver for direct steam generation",
    url: "https://www.aalborgcsp.com/business-areas/csp-power-plant-technologies/solar-tower-receiver-direct-steam",
    kind: "oem-product-page",
  }),
  oemSource({
    id: "rioglass-csp-mirrors",
    publisher: "Rioglass",
    title: "Concentrated solar power mirrors",
    url: "https://rioglass.com/es-es/productos/espejos-csp.html",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "westinghouse-ap1000",
    publisher: "Westinghouse Electric Company",
    title: "AP1000 pressurized water reactor",
    url: "https://westinghousenuclear.com/new-plants/ap1000-pwr/overview/",
    kind: "oem-product-page",
  }),
  oemSource({
    id: "ge-hitachi-bwrx300",
    publisher: "GE Hitachi Nuclear Energy",
    title: "BWRX-300 general description",
    url: "https://www.gevernova.com/content/dam/gepower-new/global/en_US/images/gas-new-site/en/bwrx-300/005N9751_Rev_BWRX-300_General_Description.pdf",
    kind: "oem-brochure",
  }),
  oemSource({
    id: "nuscale-npm",
    publisher: "NuScale Power",
    title: "NuScale Power Module technical specifications",
    url: "https://www.nuscalepower.com/hubfs/NPM-technical-specifications.pdf",
    kind: "oem-datasheet",
  }),
  oemSource({
    id: "bloom-energy-server",
    publisher: "Bloom Energy",
    title: "Bloom Energy Server datasheet",
    url: "https://www.bloomenergy.com/wp-content/uploads/bloom-energy-server-datasheet-feb-2026.pdf",
    kind: "oem-datasheet",
  }),
  oemSource({
    id: "doosan-fuel-cell-products",
    publisher: "Doosan Fuel Cell",
    title: "Fuel cell products",
    url: "https://www.doosanfuelcell.com/en/prod/prod-0102/",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "tesla-megapack",
    publisher: "Tesla",
    title: "Megapack",
    url: "https://www.tesla.com/megapack/design",
    kind: "oem-product-page",
  }),
  oemSource({
    id: "wartsila-quantum3",
    publisher: "Wärtsilä",
    title: "Quantum3 energy storage system",
    url: "https://www.wartsila.com/docs/default-source/energy-docs/energy-storage/specification-sheets/quantum3_spec-sheet.pdf",
    kind: "oem-datasheet",
  }),
  oemSource({
    id: "fluence-gridstack-pro",
    publisher: "Fluence",
    title: "Gridstack Pro",
    url: "https://info.fluenceenergy.com/hubfs/Fluence%20Gridstack%20Pro_Global_US%20EN.pdf",
    kind: "oem-datasheet",
  }),
  oemSource({
    id: "sungrow-powertitan",
    publisher: "Sungrow",
    title: "PowerTitan energy storage system",
    url: "https://info-support.sungrowpower.com/datasheet-materials/47a52e01-cda5-47d0-99cd-6b39b5729be7.pdf",
    kind: "oem-datasheet",
  }),
  oemSource({
    id: "orbital-o2",
    publisher: "Orbital Marine Power",
    title: "O2 tidal turbine technology",
    url: "https://www.orbitalmarine.com/technology/",
    kind: "oem-product-page",
  }),
  oemSource({
    id: "corpower-c4",
    publisher: "CorPower Ocean",
    title: "C4 wave energy converter",
    url: "https://corpowerocean.com/corpower-ocean-unveils-commercial-scale-products-to-unleash-utility-scale-wave-farms/",
    kind: "oem-product-page",
  }),
  oemSource({
    id: "sulzer-hpt",
    publisher: "Sulzer",
    title: "HPT high-pressure barrel casing pump",
    url: "https://www.sulzer.com/en/shared/products/hpt-high-pressure-barrel-casing-pump",
    kind: "oem-product-page",
  }),
  oemSource({
    id: "marley-nc",
    publisher: "SPX Cooling Technologies",
    title: "Marley NC cooling tower",
    url: "https://spxcooling.com/cooling-towers/marley-nc/",
    kind: "oem-product-page",
  }),
  oemSource({
    id: "alfa-laval-alfacond",
    publisher: "Alfa Laval",
    title: "AlfaCond plate condenser",
    url: "https://www.alfalaval.com/products/heat-transfer/plate-heat-exchangers/gasketed-plate-and-frame-heat-exchangers/alfacond/",
    kind: "oem-product-page",
  }),
  oemSource({
    id: "ge-vernova-hydrogen-generators",
    publisher: "GE Vernova",
    title: "Hydrogen-cooled generators",
    url: "https://www.gevernova.com/gas-power/products/generators/hydrogen-cooled",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "hitachi-energy-gsu",
    publisher: "Hitachi Energy",
    title: "Generator step-up transformers",
    url: "https://www.hitachienergy.com/us/en/products-and-solutions/transformers/power-transformers/generator-step-up-transformers-gsu",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "atlas-copco-gt",
    publisher: "Atlas Copco",
    title: "GT series integrally geared centrifugal compressors",
    url: "https://www.atlascopco.com/en-us/compressors/products/processairgasequipment/centrifugal-compressors/gt-series-centrifugal-compressor",
    kind: "oem-product-page",
  }),
  oemSource({
    id: "baker-hughes-compressors",
    publisher: "Baker Hughes",
    title: "Centrifugal and axial compressors",
    url: "https://www.bakerhughes.com/centrifugal-axial-compressors",
    kind: "oem-portfolio-page",
  }),
  oemSource({
    id: "john-cockerill-cooling",
    publisher: "John Cockerill",
    title: "Cooling systems",
    url: "https://energy.johncockerill.com/en/cooling/",
    kind: "oem-portfolio-page",
  }),
  publicSource(
    {
      id: "cec-solar-equipment-lists",
      publisher: "California Energy Commission",
      title: "Solar Equipment Lists",
      url: "https://www.energy.ca.gov/programs-and-topics/programs/solar-equipment-lists",
      kind: "regulatory-registry",
      evidenceTier: "tier-2-regulatory-registry",
      notes:
        "Manufacturer-submitted registry. Treat listing as registry evidence, not independent performance certification.",
    },
    "California state website terms; manufacturer submissions may retain third-party rights.",
  ),
  publicSource(
    {
      id: "epa-egrid",
      publisher: "United States Environmental Protection Agency",
      title: "Emissions & Generation Resource Integrated Database",
      url: "https://www.epa.gov/egrid/detailed-data",
      kind: "government-dataset",
    },
    "Public domain (United States government work).",
  ),
  publicSource(
    {
      id: "eia-860",
      publisher: "United States Energy Information Administration",
      title: "Form EIA-860 detailed data",
      url: "https://www.eia.gov/electricity/data/eia860/",
      kind: "government-dataset",
    },
    "United States government data; verify dataset-specific notices.",
  ),
  publicSource(
    {
      id: "eia-923",
      publisher: "United States Energy Information Administration",
      title: "Form EIA-923 detailed data",
      url: "https://www.eia.gov/electricity/data/eia923/",
      kind: "government-dataset",
    },
    "United States government data; verify dataset-specific notices.",
  ),
  openSource(
    {
      id: "nrel-sam",
      publisher: "National Renewable Energy Laboratory",
      title: "System Advisor Model",
      url: "https://github.com/NREL/SAM",
      kind: "open-source-model",
    },
    "BSD-3-Clause",
  ),
  publicSource(
    {
      id: "nrel-atb-2024",
      publisher: "National Renewable Energy Laboratory",
      title: "2024 Annual Technology Baseline",
      url: "https://atb.nrel.gov/electricity/2024/technologies",
      kind: "government-dataset",
    },
    "NREL/Department of Energy website and dataset terms; attribution required.",
  ),
  publicSource(
    {
      id: "nrel-gleam",
      publisher: "National Renewable Energy Laboratory",
      title: "Greenhouse Gas Life Cycle Assessment Harmonization",
      url: "https://www.nrel.gov/analysis/gleam",
      kind: "government-dataset",
    },
    "NREL/Department of Energy website and report terms; attribution required.",
  ),
  publicSource(
    {
      id: "nrel-operational-water",
      publisher: "National Renewable Energy Laboratory",
      title:
        "Operational water consumption and withdrawal factors for electricity generating technologies",
      url: "https://research-hub.nrel.gov/en/publications/operational-water-consumption-and-withdrawal-factors-for-electric-4/",
      kind: "government-dataset",
    },
    "Publication-specific terms apply; store citation metadata and derived factors only after review.",
  ),
  publicSource(
    {
      id: "epa-ap42",
      publisher: "United States Environmental Protection Agency",
      title: "AP-42 Compilation of Air Emissions Factors",
      url: "https://www.epa.gov/air-emissions-factors-and-quantification/ap-42-compilation-air-emissions-factors-stationary-sources",
      kind: "government-dataset",
    },
    "Public domain (United States government work).",
  ),
  publicSource(
    {
      id: "epa-ghg-emission-factors-2025",
      publisher: "United States Environmental Protection Agency",
      title: "GHG Emission Factors Hub 2025",
      url: "https://www.epa.gov/system/files/documents/2025-01/ghg-emission-factors-hub-2025.pdf",
      kind: "government-dataset",
    },
    "Public domain (United States government work).",
  ),
  publicSource(
    {
      id: "ipcc-2019-refinement",
      publisher: "Intergovernmental Panel on Climate Change",
      title: "2019 Refinement to the 2006 IPCC Guidelines",
      url: "https://www.ipcc-nggip.iges.or.jp/public/2019rf/",
      kind: "government-dataset",
    },
    "IPCC copyright and terms of use; attribution and source-specific reuse review required.",
  ),
] as const satisfies readonly EquipmentEvidenceSource[];

export const EQUIPMENT_SOURCE_BY_ID = new Map(
  EQUIPMENT_SOURCE_LICENSE_MANIFEST.map((source) => [source.id, source]),
);
